import React, { useEffect, useRef, useState, useMemo } from 'react';
import moment from 'moment-timezone';
import { DateRangePicker as ReactDateRangePicker, createStaticRanges } from 'react-date-range';
import { ArrowLeftShort, ArrowRightShort, CalendarDate } from 'react-bootstrap-icons';
import { Button } from 'react-bootstrap';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { enGB } from 'date-fns/locale';

const getRangePresets = () => {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return {
    Today: {
      startDate: moment
        .tz(tz)
        .startOf('day')
        .toDate(),
      endDate: moment
        .tz(tz)
        .endOf('day')
        .toDate()
    },
    'This Week': {
      startDate: moment
        .tz(tz)
        .startOf('isoWeek')
        .toDate(),
      endDate: moment
        .tz(tz)
        .endOf('isoWeek')
        .toDate()
    },
    'Last Week': {
      startDate: moment
        .tz(tz)
        .subtract(1, 'week')
        .startOf('isoWeek')
        .toDate(),
      endDate: moment
        .tz(tz)
        .subtract(1, 'week')
        .endOf('isoWeek')
        .toDate()
    },
    'This Month': {
      startDate: moment
        .tz(tz)
        .startOf('month')
        .toDate(),
      endDate: moment
        .tz(tz)
        .endOf('month')
        .toDate()
    },
    'Last Month': {
      startDate: moment
        .tz(tz)
        .subtract(1, 'month')
        .startOf('month')
        .toDate(),
      endDate: moment
        .tz(tz)
        .subtract(1, 'month')
        .endOf('month')
        .toDate()
    },
    'This Quarter': {
      startDate: moment
        .tz(tz)
        .startOf('quarter')
        .toDate(),
      endDate: moment
        .tz(tz)
        .endOf('quarter')
        .toDate()
    },
    'Last Quarter': {
      startDate: moment
        .tz(tz)
        .subtract(1, 'quarter')
        .startOf('quarter')
        .toDate(),
      endDate: moment
        .tz(tz)
        .subtract(1, 'quarter')
        .endOf('quarter')
        .toDate()
    },
    'This Year': {
      startDate: moment
        .tz(tz)
        .startOf('year')
        .toDate(),
      endDate: moment
        .tz(tz)
        .endOf('year')
        .toDate()
    },
    'Last 3 Months': {
      startDate: moment
        .tz(tz)
        .subtract(3, 'month')
        .startOf('day')
        .toDate(),
      endDate: moment
        .tz(tz)
        .endOf('day')
        .toDate()
    },
    'Last 12 Months': {
      startDate: moment
        .tz(tz)
        .subtract(12, 'month')
        .startOf('day')
        .toDate(),
      endDate: moment
        .tz(tz)
        .endOf('day')
        .toDate()
    },
    'Last Year': {
      startDate: moment
        .tz(tz)
        .subtract(1, 'year')
        .startOf('year')
        .toDate(),
      endDate: moment
        .tz(tz)
        .subtract(1, 'year')
        .endOf('year')
        .toDate()
    },
    'All Time': {
      startDate: null,
      endDate: null
    }
  };
};

const AdvanceDateRangePicker = ({
  value,
  onChange,
  className = '',
  customView,
  FooterContent,
  closeOnSelect = true,
  showNavigator = false,
  handleNavigateDate = () => {},
  rangeType = 'day',
  isFloating = false,
  topOffset = 0,
  allowAllTime = true,
  extraStyleForSlideBarFilter = false,
  singleDateMode = false
}) => {
  const [showPicker, setShowPicker] = useState(false);
  const triggerRef = useRef(null);
  const [portalPosition, setPortalPosition] = useState({ top: 0, left: 0 });
  const [extraStyleForNonFloating, setExtraStyleForNonFloating] = useState({ right: 0 });

  const availablePresets = useMemo(() => {
    const presets = getRangePresets();
    if (allowAllTime) return presets;
    const { 'All Time': _omit, ...rest } = presets;
    return rest;
  }, [allowAllTime]);

  const staticRanges = useMemo(() => {
    return createStaticRanges([
      ...Object.keys(availablePresets).map(key => ({
        label: key,
        range: () => ({
          ...availablePresets[key],
          isPresetRange: true,
          rangeType: key
        }),
        isSelected(range) {
          const definedRange = this.range();
          if (!definedRange.startDate || !definedRange.endDate) {
            return !range.startDate && !range.endDate;
          }
          return (
            moment(range.startDate).isSame(definedRange.startDate, 'day') &&
            moment(range.endDate).isSame(definedRange.endDate, 'day')
          );
        }
      }))
    ]);
  }, [availablePresets]);

  const coerceDate = d => (d ? (d instanceof Date ? d : new Date(d)) : null);
  const pickerRange = {
    ...value,
    startDate: coerceDate(value?.startDate),
    endDate: coerceDate(value?.endDate)
  };

  useEffect(() => {
    const handleClickOutside = event => {
      const dateRangePicker = document.getElementById('custom-date-range-picker');
      const clickedInsidePicker = dateRangePicker && dateRangePicker.contains(event.target);
      const clickedInsideTrigger = triggerRef.current && triggerRef.current.contains(event.target);
      if (!clickedInsidePicker && !clickedInsideTrigger) {
        setShowPicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const openPicker = () => {
    if (showPicker) return;
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const top = rect.top + rect.height / 2 + topOffset;
      const left = rect.left - 16; // small gap from

      if (isFloating) {
        setPortalPosition({ top, left });
      } else {
        const extraStyle = rect.left >= 230 ? { right: 0 } : { left: 0 };
        setExtraStyleForNonFloating(extraStyle);
      }
    }
    setShowPicker(true);
  };

  // Keep the floating panel positioned correctly on scroll/resize
  useEffect(() => {
    if (!isFloating || !showPicker) return;

    const updatePosition = () => {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      const top = rect.top + rect.height / 2 + topOffset;
      const left = rect.left - 16;
      setPortalPosition({ top, left });
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isFloating, showPicker, topOffset]);

  // Hide sidebar and prevent range selection in singleDateMode, and reduce sidebar width
  useEffect(() => {
    if (!showPicker) return;

    const styleId = 'date-range-picker-sidebar-styles';
    let styleElement = document.getElementById(styleId);

    if (!styleElement) {
      styleElement = document.createElement('style');
      styleElement.id = styleId;
      document.head.appendChild(styleElement);
    }

    if (singleDateMode) {
      styleElement.textContent = `
        #custom-date-range-picker .rdrDefinedRangesWrapper {
          display: none !important;
        }
      `;
    } else {
      styleElement.textContent = `
        #custom-date-range-picker .rdrDefinedRangesWrapper {
          width: 180px !important;
          min-width: 180px !important;
        }
        #custom-date-range-picker .rdrDefinedRangesWrapper .rdrStaticRange {
          padding: 2px 2px !important;
          font-size: 13px !important;
        }
        #custom-date-range-picker .rdrDefinedRangesWrapper .rdrStaticRangeLabel {
          font-size: 13px !important;
        }
      `;
    }

    return () => {
      if (styleElement && styleElement.parentNode) {
        styleElement.parentNode.removeChild(styleElement);
      }
    };
  }, [singleDateMode, showPicker]);

  return (
    <div className={className} style={{ position: 'relative' }} ref={triggerRef}>
      {customView ? (
        customView({ openCalendar: () => !showPicker && setShowPicker(true) })
      ) : (
        <div
          className={`d-flex justify-content-between align-items-center ${extraStyleForSlideBarFilter ? ' w-100' : ''}`}
          style={{ gap: 4 }}
        >
          {showNavigator && rangeType !== 'All Time' && (
            <>
              <Button
                size="md"
                variant={'border text-dark'}
                className="px-1 py-1.5 d-flex justify-content-center align-items-center border bg-white"
                style={{ width: '50px', height: '38px' }}
                onClick={() => handleNavigateDate(rangeType, 'Previous')}
              >
                <ArrowLeftShort size={24} />
              </Button>

              <Button
                size="md"
                variant={'border text-dark'}
                className="px-1 py-1.5 d-flex justify-content-center align-items-center border bg-white"
                style={{ width: '50px', height: '38px' }}
                onClick={() => handleNavigateDate(rangeType, 'Next')}
              >
                <ArrowRightShort size={24} />
              </Button>
            </>
          )}
          <div
            className={`border hover d-flex align-items-center bg-white h-100 ${
              extraStyleForSlideBarFilter ? 'py-1 px-3' : 'px-2 py-1'
            }  ${showPicker ? 'border-dark' : ''}`}
            style={{
              borderRadius: extraStyleForSlideBarFilter ? 10 : 8,
              width: extraStyleForSlideBarFilter ? '100%' : value.startDate && value.endDate ? 220 : 130,
              minHeight: '27px'
            }}
            onClick={openPicker}
          >
            <CalendarDate className="me-2" size={14} style={{ flexShrink: 0 }} />
            <div
              style={{
                width: 1,
                height: 18
              }}
              className="bg-dark mx-2"
            />
            <span className="smallFont py-1" style={{ whiteSpace: 'nowrap', flex: 1, textAlign: 'left' }}>
              {singleDateMode
                ? value?.startDate
                  ? moment(value?.startDate).format('MMM D, YYYY')
                  : 'Select Date'
                : value?.startDate && value?.endDate
                ? moment(value?.startDate).year() === 1970
                  ? `Upto ${moment(value?.endDate).format('MMM D, YYYY')}`
                  : `${moment(value?.startDate).format('MMM D, YYYY')} - ${moment(value?.endDate).format(
                      'MMM D, YYYY'
                    )}`
                : allowAllTime
                ? 'All Time'
                : 'Select Range'}
            </span>
          </div>
        </div>
      )}

      {showPicker && (
        <div
          className="border bg-light"
          id="custom-date-range-picker"
          style={
            isFloating
              ? {
                  position: 'fixed',
                  top: portalPosition.top,
                  left: portalPosition.left,
                  transform: 'translate(-100%, -60%)',
                  zIndex: 99999,
                  borderColor: '#ced4da',
                  borderRadius: 6,
                  boxShadow: '0 6px 24px rgba(0,0,0,0.12)',
                  fontSize: 14
                }
              : {
                  position: 'absolute',
                  top: '105%',
                  zIndex: 999,
                  borderColor: '#ced4da',
                  borderRadius: 6,
                  boxShadow: '0 6px 24px rgba(0,0,0,0.12)',
                  fontSize: 16,
                  ...extraStyleForNonFloating
                }
          }
        >
          <ReactDateRangePicker
            editableDateInputs={false}
            onChange={range => {
              if (singleDateMode && range?.selection?.startDate) {
                // In single date mode, set endDate to the same as startDate
                const singleDate = range.selection.startDate;
                onChange({
                  ...range,
                  selection: {
                    ...range.selection,
                    startDate: singleDate,
                    endDate: singleDate
                  }
                });
              } else {
                onChange(range);
                if (range?.selection?.isPresetRange && closeOnSelect) {
                  setShowPicker(false);
                }
              }
            }}
            moveRangeOnFirstSelection={!singleDateMode}
            ranges={[pickerRange]}
            staticRanges={singleDateMode ? [] : staticRanges}
            inputRanges={[]}
            showSelectionPreview={!singleDateMode}
            direction="vertical"
            footerContent={FooterContent && FooterContent({ setShowPicker })}
            locale={enGB}
          />
        </div>
      )}
    </div>
  );
};

export default AdvanceDateRangePicker;
